import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { blogPosts } from "../data/blogData";
import {
  FaArrowLeft,
  FaCalendar,
  FaClock,
  FaHeart,
  FaGithub,
  FaLinkedin,
  FaShare,
  FaTwitter,
  FaBookmark,
  FaTag,
} from "react-icons/fa";
import UtterancesComments from "../components/UtterancesComments";

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  useEffect(() => {
    const foundPost = blogPosts.find((p) => p.slug === slug);
    if (foundPost) {
      setPost(foundPost);
      setLikes(foundPost.likes || 0);
    }
    setLoading(false);
    window.scrollTo(0, 0);
  }, [slug]);

  const handleLike = () => {
    if (!liked) {
      setLikes((prev) => prev + 1);
      setLiked(true);
    } else {
      setLikes((prev) => prev - 1);
      setLiked(false);
    }
  };

  const handleLinkedInShare = () => {
    const baseUrl = "https://www.waisnazari.me";
    const postUrl = `${baseUrl}/blog/${post.slug}`;
    const url = encodeURIComponent(postUrl);
    const title = encodeURIComponent(post.title);
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}`,
      "_blank"
    );
  };

  const handleTwitterShare = () => {
    const baseUrl = "https://www.waisnazari.me";
    const postUrl = `${baseUrl}/blog/${post.slug}`;
    const url = encodeURIComponent(postUrl);
    const text = encodeURIComponent(`Check out this article: ${post.title}`);
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      "_blank"
    );
  };

  const handleShare = () => {
    const baseUrl = "https://www.waisnazari.me";
    const postUrl = `${baseUrl}/blog/${post.slug}`;
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: postUrl,
      });
    } else {
      navigator.clipboard.writeText(postUrl);
      alert("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Article not found
          </h2>
          <Link to="/blog" className="text-emerald-600 hover:text-emerald-700">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="relative min-h-screen overflow-x-hidden pt-20 sm:pt-24 pb-12 bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none" />
      <div className="absolute top-20 -left-20 w-72 h-72 bg-emerald-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob" />
      <div className="absolute bottom-20 -right-20 w-72 h-72 bg-cyan-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000" />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000" />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Navigation */}
        <nav className="mb-6 sm:mb-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-emerald-600 transition-colors group"
          >
            <FaArrowLeft className="text-sm transition-transform group-hover:-translate-x-1" />
            <span>Back to Blog</span>
          </Link>
        </nav>

        {/* Article Header with Hero Image */}
        <header className="relative bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden mb-8">
          <div className="relative h-[250px] sm:h-[400px] md:h-[500px] lg:h-[600px]">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 md:p-10 lg:p-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-wrap items-center gap-3 mb-3 sm:mb-4">
                <span className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-semibold uppercase tracking-wider rounded-full shadow-sm">
                  {post.category}
                </span>
                <span className="flex items-center gap-1.5 text-white/70 text-sm font-medium tracking-wide">
                  <FaCalendar className="text-[10px]" />
                  {post.date}
                </span>
                <span className="flex items-center gap-1.5 text-white/70 text-sm font-medium tracking-wide">
                  <FaClock className="text-[10px]" />
                  {post.readTime} min read
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight tracking-tight drop-shadow-xl mb-3">
                {post.title}
              </h1>
              <p className="text-white/90 text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed max-w-3xl drop-shadow-lg font-light">
                {post.excerpt}
              </p>
            </div>
          </div>
        </header>

        {/* Dynamic Content with Responsive Typography */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden mb-8">
          <div className="p-4 sm:p-6 md:p-8 lg:p-10">
            <div className="prose prose-sm sm:prose-base md:prose-lg lg:prose-xl dark:prose-invert max-w-none">
              <div dangerouslySetInnerHTML={{ __html: post.content }} />
            </div>
          </div>
        </div>

        {/* Author & Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4 py-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg shadow-sm">
              {post.author.charAt(0)}
            </div>
            <div>
              <div className="text-base font-semibold text-gray-900 dark:text-white">
                {post.author}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Security Engineer & Developer
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
                liked
                  ? "border-red-200 bg-red-50 text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
                  : "border-gray-200 bg-transparent text-gray-600 hover:border-red-300 hover:text-red-500 dark:border-gray-700 dark:text-gray-400 dark:hover:border-red-700"
              }`}
            >
              <FaHeart className={liked ? "fill-current" : ""} />
              <span className="text-sm font-medium">{likes}</span>
            </button>
            <button
              onClick={() => setBookmarked(!bookmarked)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
                bookmarked
                  ? "border-amber-200 bg-amber-50 text-amber-600 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400"
                  : "border-gray-200 bg-transparent text-gray-600 hover:border-amber-300 hover:text-amber-500 dark:border-gray-700 dark:text-gray-400 dark:hover:border-amber-700"
              }`}
            >
              <FaBookmark className={bookmarked ? "fill-current" : ""} />
            </button>
            {post.githubRepo && (
              <a
                href={post.githubRepo}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-transparent text-gray-600 hover:border-gray-400 hover:text-gray-900 transition-all duration-200 dark:border-gray-700 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:text-white"
              >
                <FaGithub />
              </a>
            )}
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 py-6 border-b border-gray-200 dark:border-gray-700">
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-200 bg-gray-50 text-sm text-gray-700 transition-all duration-200 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300 dark:hover:border-emerald-500 dark:hover:bg-emerald-900/20 dark:hover:text-emerald-400"
            >
              <FaTag className="text-[10px]" />
              {tag}
            </span>
          ))}
        </div>

        {/* Share Buttons */}
        <div className="flex flex-wrap items-center gap-4 py-6 border-b border-gray-200 dark:border-gray-700">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Share this article:
          </span>
          <div className="flex gap-3">
            <button
              onClick={handleLinkedInShare}
              className="p-2.5 rounded-full border border-gray-200 bg-transparent text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 dark:border-gray-700 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:border-blue-700"
            >
              <FaLinkedin />
            </button>
            <button
              onClick={handleTwitterShare}
              className="p-2.5 rounded-full border border-gray-200 bg-transparent text-sky-600 hover:bg-sky-50 hover:border-sky-300 transition-all duration-200 dark:border-gray-700 dark:text-sky-400 dark:hover:bg-sky-900/20 dark:hover:border-sky-700"
            >
              <FaTwitter />
            </button>
            <button
              onClick={handleShare}
              className="p-2.5 rounded-full border border-gray-200 bg-transparent text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-all duration-200 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:border-gray-500"
            >
              <FaShare />
            </button>
          </div>
        </div>

        {/* Comments */}
        <section id="comments" className="pt-8">
          <UtterancesComments postId={post.id.toString()} />
        </section>
      </div>

      <style jsx>{`
        .bg-grid-pattern {
          background-image:
            linear-gradient(to right, #d1d5db 1px, transparent 1px),
            linear-gradient(to bottom, #d1d5db 1px, transparent 1px);
          background-size: 40px 40px;
        }
        .dark .bg-grid-pattern {
          background-image:
            linear-gradient(to right, #374151 1px, transparent 1px),
            linear-gradient(to bottom, #374151 1px, transparent 1px);
        }
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>
    </article>
  );
};

export default BlogPost;